import { FC, useEffect, useMemo, useState } from "react";
import { InformationCircleIcon } from "@heroicons/react/24/outline";
import { useQueryClient } from "@tanstack/react-query";
import { useGummiwormContext } from "@/context/GummiwormContext";
import {
  competitionSessionKey,
  setHandleVisibility,
  useCompetitionSession,
} from "@/hooks/useCompetitionAuth";
import { getTokenDecimals } from "@/lib/tokenMetadata";
import type { Leaderboard as LeaderboardData, LeaderboardEntry } from "@/lib/wsTypes";

/** Scores in the "value" categories are denominated in the numeraire. */
const NUMERAIRE = "VAN";

/**
 * Live competition leaderboards. Categories, their display names, and how
 * each score is formatted. Growth categories are fractions; standings are
 * portfolio values; positive_days is a count. The internal `max_drawdown`
 * list (the HOSKY "biggest losers" pool) is deliberately NOT listed here —
 * it must not appear in the participant UI.
 */
type Format = "value" | "percent" | "count";

const CATEGORIES: { key: string; label: string; format: Format; tip: string }[] = [
  {
    key: "final_standing",
    label: "Grand Standing",
    format: "value",
    tip: "Highest overall portfolio value, in Vanilla, averaged over the final hours of the competition.",
  },
  {
    key: "week1_growth",
    label: "Week 1",
    format: "percent",
    tip: "Largest portfolio growth during week one, measured against your starting bundle.",
  },
  {
    key: "week2_growth",
    label: "Week 2",
    format: "percent",
    tip: "Largest portfolio growth during week two, measured from your week-one closing value. Everyone is eligible.",
  },
  {
    key: "rookie_week2",
    label: "Rookie (Week 2)",
    format: "percent",
    tip: "Best week-two growth among players who joined after day six.",
  },
  {
    key: "comeback",
    label: "Comeback",
    format: "percent",
    tip: "Best week-two growth among players who sat in the bottom half of the standings at the end of week one.",
  },
  {
    key: "steady_hand",
    label: "Steady Hand",
    format: "value",
    tip: "Highest standing among players whose worst peak-to-trough drawdown stayed under the cap. Rewards steady returns over big swings.",
  },
  {
    key: "maker_profit",
    label: "Market Maker",
    format: "value",
    tip: "Most profit earned from resting limit orders that other traders filled.",
  },
  {
    key: "positive_days",
    label: "Iron Stomach",
    format: "count",
    tip: "Most days finishing in the green. A minimum level of trading activity is required to qualify.",
  },
];

function shortAccount(id: string): string {
  const hex = id.replace(/^0x/, "");
  return `${hex.slice(0, 4)}…${hex.slice(-4)}`;
}

function formatScore(score: number, format: Format): string {
  if (format === "percent") {
    const pct = score * 100;
    return `${pct >= 0 ? "+" : ""}${pct.toFixed(1)}%`;
  }
  if (format === "count") return Math.round(score).toString();

  // Value scores arrive in VAN's smallest unit, so they need the numeraire's
  // decimals applied before anything else. Rendered raw, a 10,000 VAN bundle
  // reads as "10.0B".
  const units = score / 10 ** getTokenDecimals(NUMERAIRE);
  const abs = Math.abs(units);
  const sign = units < 0 ? "-" : "";
  // Every account starts from the same bundle, so what distinguishes players
  // is the last few VAN. Compact notation rounds that away and shows the whole
  // field the same number; keep full precision up to a million.
  if (abs >= 1e9) return `${sign}${(abs / 1e9).toFixed(2)}B`;
  if (abs >= 1e6) return `${sign}${(abs / 1e6).toFixed(2)}M`;
  return `${sign}${abs.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

const rankColor = ["text-sr-lemon-muted", "text-sr-neutral-300", "text-sr-tangerine"];

const CategoryCard: FC<{
  label: string;
  format: Format;
  tip: string;
  board: LeaderboardData | undefined;
  myAccount: string | null;
  limit?: number;
}> = ({ label, format, tip, board, myAccount, limit = 5 }) => {
  const entries = board?.entries ?? [];
  const myHex = myAccount?.replace(/^0x/, "").toLowerCase();
  return (
    <div className="sr-panel flex flex-col overflow-visible">
      <div className="flex items-center justify-between gap-2 border-b border-sr-border-subtle px-4 py-2.5">
        <h3 className="text-[11px] font-semibold uppercase tracking-wide text-sr-salmon">{label}</h3>
        <span className="group/tip relative flex" tabIndex={0} aria-label={`${label} rules: ${tip}`}>
          <InformationCircleIcon className="h-4 w-4 cursor-help text-sr-neutral-500 transition-colors hover:text-sr-neutral-300 group-focus-within/tip:text-sr-neutral-300" />
          <span className="pointer-events-none absolute right-0 top-6 z-30 hidden w-56 rounded-lg border border-sr-border-subtle bg-sr-surface-raised p-3 text-xs leading-relaxed text-sr-neutral-300 shadow-lg group-hover/tip:block group-focus-within/tip:block">
            {tip}
          </span>
        </span>
      </div>
      <div className="flex flex-col px-2 py-1.5">
        {entries.length === 0 ? (
          <div className="px-2 py-3 text-xs text-sr-neutral-500">No entries yet</div>
        ) : (
          entries.slice(0, limit).map((entry: LeaderboardEntry, i: number) => {
            const isMe = myHex && entry.accountId.replace(/^0x/, "").toLowerCase() === myHex;
            return (
              <div
                key={entry.accountId}
                className={`flex items-center justify-between rounded-md px-2 py-1 text-sm ${
                  isMe ? "bg-sr-lemon-muted/10" : ""
                }`}
              >
                <span className="flex items-center gap-2 tabular-nums">
                  <span className={`w-4 text-right text-xs font-semibold ${rankColor[i] ?? "text-sr-neutral-500"}`}>
                    {i + 1}
                  </span>
                  <span className={`text-xs ${entry.handle ? "" : "font-mono"} ${isMe ? "text-white" : "text-sr-neutral-300"}`}>
                    {entry.handle ? `@${entry.handle}` : shortAccount(entry.accountId)}
                    {isMe && <span className="ml-1 text-[10px] text-sr-lemon-muted">you</span>}
                  </span>
                </span>
                <span
                  className={`tabular-nums font-medium ${
                    format === "percent"
                      ? entry.score >= 0
                        ? "text-sr-mint"
                        : "text-sr-salmon"
                      : "text-white"
                  }`}
                >
                  {formatScore(entry.score, format)}
                </span>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

/** Signed-in users can flip whether their X handle shows on the leaderboard,
 * reflecting and updating the server-side consent. Hidden when not signed in. */
const HandleVisibilityToggle: FC = () => {
  const { data: session } = useCompetitionSession();
  const queryClient = useQueryClient();
  const [checked, setChecked] = useState(false);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    setChecked(session?.showHandle ?? false);
  }, [session?.showHandle]);

  if (!session?.authenticated) return null;

  const onToggle = async (next: boolean) => {
    setChecked(next); // optimistic
    setSaving(true);
    try {
      await setHandleVisibility(next);
      queryClient.invalidateQueries({ queryKey: competitionSessionKey });
    } catch {
      setChecked(!next); // revert on failure
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex items-center gap-2.5 text-xs text-sr-neutral-400">
      <span>Show my handle{session.handle ? ` (@${session.handle})` : ""}</span>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        aria-label="Show my handle on the public leaderboard"
        disabled={saving}
        onClick={() => onToggle(!checked)}
        className={`sr-focus-ring relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors disabled:opacity-50 ${
          checked ? "bg-sr-lemon-muted" : "bg-sr-surface-raised"
        }`}
      >
        <span
          className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow-sm transition-transform ${
            checked ? "translate-x-[18px]" : "translate-x-0.5"
          }`}
        />
      </button>
    </div>
  );
};

type Tab = "competition" | "staff" | "bots";

export const Leaderboard: FC = () => {
  const { ledgerView, myAccountId } = useGummiwormContext();
  const boards = (ledgerView?.leaderboards ?? {}) as Record<string, LeaderboardData>;
  const myAccount = myAccountId ? String(myAccountId) : null;

  const available = useMemo(
    () => CATEGORIES.filter((c) => boards[c.key]),
    [boards],
  );
  const hasAny = available.length > 0;

  // Staff/bots boards ship from newer aggregators only; show a tab only when
  // its board exists, so an older backend just shows the competition grid.
  const hasStaff = !!boards["staff"];
  const hasBots = !!boards["bots"];
  const [tab, setTab] = useState<Tab>("competition");
  const activeTab = (tab === "staff" && !hasStaff) || (tab === "bots" && !hasBots) ? "competition" : tab;

  const tabs: { key: Tab; label: string }[] = [
    { key: "competition", label: "Competition" },
    ...(hasStaff ? [{ key: "staff" as Tab, label: "Staff" }] : []),
    ...(hasBots ? [{ key: "bots" as Tab, label: "Bots" }] : []),
  ];

  return (
    <section className="w-full max-w-4xl" aria-label="Competition leaderboards">
      <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-white">Leaderboards</h1>
          <p className="mt-0.5 text-sm text-sr-neutral-500">
            Live standings across every prize. Provisional — final winners are subject to review.
          </p>
        </div>
        <HandleVisibilityToggle />
      </div>

      {tabs.length > 1 && (
        <div className="mb-3 flex items-center gap-1 rounded-full bg-sr-surface p-0.5 w-fit">
          {tabs.map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`sr-focus-ring rounded-full px-3 py-1 text-xs font-medium transition-colors ${
                activeTab === t.key
                  ? "bg-sr-surface-raised text-white"
                  : "text-sr-neutral-500 hover:text-sr-neutral-300"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
      )}

      {activeTab === "competition" ? (
        !hasAny ? (
          <div className="sr-panel px-5 py-12 text-center text-sm text-sr-neutral-500">
            Standings appear once the competition is live.
          </div>
        ) : (
          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {available.map((c) => (
              <CategoryCard
                key={c.key}
                label={c.label}
                format={c.format}
                tip={c.tip}
                board={boards[c.key]}
                myAccount={myAccount}
              />
            ))}
          </div>
        )
      ) : (
        <div>
          <p className="mb-2 text-xs text-sr-neutral-500">
            Shown for fun — {activeTab === "staff" ? "Sundae staff" : "house bots"} are not eligible for prizes.
          </p>
          <div className="sm:max-w-sm">
            <CategoryCard
              label={activeTab === "staff" ? "Staff" : "Bots"}
              format="value"
              tip={
                activeTab === "staff"
                  ? "Sundae team accounts, ranked by portfolio value. Not eligible for prizes."
                  : "House market-maker bots, ranked by portfolio value. Not eligible for prizes."
              }
              board={boards[activeTab]}
              myAccount={myAccount}
              limit={20}
            />
          </div>
        </div>
      )}
    </section>
  );
};
